import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { Box, Grid } from '@material-ui/core';
import CustomizeLayoutQuiz from 'component/layout_quiz/layout_quiz';
import { SelectItemOverlay } from 'component/layout_quiz/select_item_overlay';
import EmptyAnswer from 'component/StandardAlert/no_answer';
import { User } from 'component/stylist_profile_header/types';
import { useAuth } from 'contexts/AuthContext';
import { GET_USER_BY_USERNAME } from 'graph_ql/users/queries';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { setSkinUndertoneDefault } from 'redux/authentication/user_detail';
import { EQuestionId, EQuizId, ESubjectQuestion, quizContextType } from 'types/quiz';
import { CREATE_ANSWER } from '../../../graph_ql/getQuiz/mutation';
import { GET_QUESTION_DATA, GET_QUIZ_RESPONSE } from '../../../graph_ql/getQuiz/queries';
import { useGetGender } from '../../../hooks/useGetGender';
import { PATH } from '../../../routes/constant';
import { getSkinUndertoneValue } from '../../../utils/utils';
import { useStyles } from './styles';
import useValidateAnswers from '../hooks/useValidateAnswers';
export interface ISkinUndertoneProps {}

const SkinUndertone = (props: { hideTitle?: boolean }) => {
    const { userName } = useParams<{ userName: string }>();
    const { currentUser } = useAuth();
    const history = useHistory();
    const gender = useGetGender(Boolean(userName));
    const location = useLocation();
    const dispatch = useDispatch();
    const classes = useStyles();
    const [quizData, setQuizData] = useState<any>(null);
    const [requiredQuestion, setRequiredQuestion] = useState(true);
    const [user, setUser] = useState<User>();
    const [defaultValueSkinUndertone, setDefaultValueSkinUndertone] = useState<any>(null);
    const isUpdate = location.pathname.includes(PATH.MY_STYLE_PREFERENCES);
    const [answerAlreadyUpdated, setAnswerUpdated] = useState(false);

    const [getQuizResponse, { loading: loadingQuiz }] = useLazyQuery(GET_QUIZ_RESPONSE, {
        onCompleted(data) {
            setRequiredQuestion(data.getQuizResponse?.questionsInfo[0]?.required);
            setDefaultValueSkinUndertone(data.getQuizResponse?.answers?.skin_undertone_question);
            setQuizData(data.getQuizResponse);
        },
    });
    const [getUserByUserName, { loading: userLoading }] = useLazyQuery(GET_USER_BY_USERNAME, {
        onCompleted(data) {
            setUser(data.getUserByUserName);
            getQuizResponse({
                variables: {
                    input: {
                        quizId: EQuizId.SKIN_UNDERTONE,
                        contextType: quizContextType,
                        contextId: EQuizId.SKIN_UNDERTONE,
                        userId: data.getUserByUserName?.id,
                    },
                },
            });
        },
    });

    const createAnswer = (input: {
        questionId: String;
        questionSubject: String;
        contextType: String;
        contextId: String;
        value: String;
        valueType: String;
        isSkipped?: boolean;
    }) => {
        const variables = {
            input: {
                ...input,
                ...(defaultValueSkinUndertone ? { id: defaultValueSkinUndertone.id } : {}),
            },
        };
        answerMutation({ variables });
    };
    const [answerMutation, { loading: loadingAnswer }] = useMutation(CREATE_ANSWER, {
        onCompleted(response) {
            onAnswerCompleted(response);
            dispatch(setSkinUndertoneDefault(response.createAnswer));
        },
        onError(error) {
            console.log('Create Failed: ', error);
        },
    });

    const onAnswerCompleted = async (response: any) => {
        await getQuizResponse({
            variables: {
                input: {
                    quizId: EQuizId.SKIN_UNDERTONE,
                    contextType: quizContextType,
                    contextId: EQuizId.SKIN_UNDERTONE,
                    userId: currentUser.uid || gender?.id,
                },
            },
        });
        if (location.pathname === PATH.QUIZ_MANAGER) {
            history.push(`${PATH.QUIZ_MANAGER}/?signup=${Date.now()}`);
        }
    };
    const { data: skinUndertoneData, loading } = useQuery(GET_QUESTION_DATA, {
        variables: {
            input: {
                subject: ESubjectQuestion.SKIN_UNDERTONE,
                paging: { page: 0, size: 0 },
                enabled: true,
            },
        },
        skip: Boolean(!userName) ? !gender : !user,
    });

    const listSkinUndertones = useMemo(
        () => (skinUndertoneData?.getQuestionData?.data || []).map(getSkinUndertoneValue),
        [skinUndertoneData?.getQuestionData?.data],
    );

    const [listSkinUndertoneOptions, setListSkinUndertoneOptions] = useState(listSkinUndertones || []);

    useEffect(() => {
        if ((currentUser && Boolean(!userName)) || gender) {
            getQuizResponse({
                variables: {
                    input: {
                        quizId: EQuizId.SKIN_UNDERTONE,
                        contextType: quizContextType,
                        contextId: EQuizId.SKIN_UNDERTONE,
                        userId: currentUser.uid || gender?.id,
                    },
                },
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentUser.uid, gender, userName]);

    useEffect(() => {
        if (userName) {
            getUserByUserName({
                variables: {
                    input: {
                        userName,
                    },
                },
            });
        }
    }, [userName, getUserByUserName]);

    useEffect(() => {
        let listSkinUndertonesCurrent: any = [];
        if (listSkinUndertones && listSkinUndertones.length > 0 && !loadingQuiz) {
            listSkinUndertonesCurrent = [...listSkinUndertones];
            const defaultValueIndex = listSkinUndertonesCurrent.findIndex(
                (item) => item.value.toLowerCase() === defaultValueSkinUndertone?.value?.toLowerCase(),
            );
            if (defaultValueIndex > -1) listSkinUndertonesCurrent[defaultValueIndex].isSelect = true;
            setListSkinUndertoneOptions(listSkinUndertonesCurrent);
        }
    }, [defaultValueSkinUndertone, defaultValueSkinUndertone?.value, listSkinUndertones, loadingQuiz]);

    const handleSelectItem = (item) => {
        setAnswerUpdated(true);
        if (Boolean(!userName)) {
            setListSkinUndertoneOptions(
                listSkinUndertones.map((d) => {
                    if (item.id === d.id) {
                        d.isSelect = true;
                    } else {
                        d.isSelect = false;
                    }
                    return d;
                }),
            );
        }
    };

    const input = {
        questionId: EQuestionId.SKIN_UNDERTONE,
        questionSubject: ESubjectQuestion.SKIN_UNDERTONE,
        contextType: quizContextType,
        contextId: EQuizId.SKIN_UNDERTONE,
        valueType: 'STRING',
    };

    const handleClickAnswer = () => {
        const currentItemSelected = listSkinUndertoneOptions.find((item) => item.isSelect);
        if (Boolean(!userName) && currentItemSelected) {
            createAnswer({ ...input, value: currentItemSelected.value });
        }
    };

    const handleSkip = () => {
        if (Boolean(!userName)) {
            createAnswer({ ...input, value: '', isSkipped: true });
        }
    };

    const valueSkinUndertone = useMemo(
        () => listSkinUndertoneOptions?.find((d) => d.isSelect),
        [listSkinUndertoneOptions],
    );

    const isValidAnswer = useValidateAnswers(
        isUpdate,
        listSkinUndertones?.find((item) => item.isSelect)?.value,
        answerAlreadyUpdated,
    );

    return (
        <CustomizeLayoutQuiz
            quizId={EQuizId.SKIN_UNDERTONE}
            title={Boolean(!userName) ? quizData?.subTitle : ''}
            titlePage={!props.hideTitle && quizData?.title}
            handleAnswer={handleClickAnswer}
            isUpdate={location.pathname.includes(PATH.MY_STYLE_PREFERENCES)}
            isDisplayContent={Boolean(!userName)}
            hideBottomNav={location.pathname === PATH.QUIZ_MANAGER}
            requiredQuestion={requiredQuestion}
            handleSkipQuestion={handleSkip}
            loading={loadingAnswer}
            nextBtnDisabled={!isValidAnswer}
            containerLoading={loading || loadingQuiz || userLoading}
        >
            <Box textAlign={'center'} width={'100%'} height={'100%'} display={'flex'} flexDirection={'column'}>
                <Box className={classes.quizRowContainer}>
                    {Boolean(!user) ? (
                        <Grid container className={classes.quizRow} spacing={2}>
                            {listSkinUndertoneOptions.length > 0 &&
                                !loading &&
                                listSkinUndertoneOptions?.map((item, index) => (
                                    <Grid item xs={4} key={index}>
                                        <Box
                                            width={'100%'}
                                            style={{
                                                border: `${
                                                    item.isSelect ? '3px solid black' : '3px solid transparent'
                                                }`,
                                                borderRadius: 8,
                                                background: `${item.isSelect ? 'black' : 'none'}`,
                                            }}
                                            borderRadius={'8px'}
                                            onClick={() => handleSelectItem(item)}
                                        >
                                            <Box
                                                className={classes.colorItem}
                                                style={{
                                                    borderRadius: 8,
                                                    backgroundImage: `linear-gradient(to bottom, ${item.gradientColors})`,
                                                }}
                                            >
                                                {item.isSelect && <SelectItemOverlay />}
                                            </Box>
                                            <p
                                                className={classes.itemTitle}
                                                style={{
                                                    color: item.isSelect ? '#F2F2F2' : '#1A1A1A',
                                                }}
                                            >
                                                {item.title}
                                            </p>
                                            {/* <p className={classes.itemSubTitle}>{item.subTitle}</p> */}
                                        </Box>
                                    </Grid>
                                ))}
                        </Grid>
                    ) : valueSkinUndertone ? (
                        <Box width={'90%'} className={classes.wrapperItem} margin="0 auto">
                            <Box
                                className={classes.colorItem}
                                style={{
                                    backgroundImage: `linear-gradient(to bottom, ${valueSkinUndertone.gradientColors})`,
                                    border: `3px solid`,
                                    height: 150,
                                }}
                            ></Box>
                            <p className={classes.itemTitle}>{valueSkinUndertone.title}</p>
                            {/* <p className={classes.itemSubTitle}>{valueSkinUndertone.subTitle}</p> */}
                        </Box>
                    ) : (
                        <EmptyAnswer title={'Skin Undertone'} userName={user?.firstName} />
                    )}
                </Box>
            </Box>
        </CustomizeLayoutQuiz>
    );
};
export default SkinUndertone;
